
import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { getCurrentUser } from './store/slices/authSlice';
import AppRoutes from './routes/AppRoutes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const INK = '#1A202C';
const INK_SOFT = '#2D3748';
const MUTED = '#4A5568';
const SUBTLE = '#718096';
const HAIRLINE = '#E2E8F0';
const SURFACE = '#FFFFFF';
const CANVAS = '#F7F8FA';
const PRIMARY = '#D4969B';
const PRIMARY_DARK = '#B97075';
const PRIMARY_LIGHT = '#F5D5D8';
const SECONDARY = '#B8860B';
const SIDEBAR_BG = '#111827';

const shadow = {
  sm: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 1px rgba(15, 23, 42, 0.02)',
  md: '0 4px 12px -2px rgba(15, 23, 42, 0.06), 0 2px 4px -1px rgba(15, 23, 42, 0.04)',
  lg: '0 12px 24px -8px rgba(15, 23, 42, 0.10), 0 4px 8px -2px rgba(15, 23, 42, 0.05)',
  focus: `0 0 0 3px ${alpha(PRIMARY, 0.25)}`,
};

const theme = createTheme({
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
  palette: {
    mode: 'light',
    primary: {
      main: PRIMARY,
      light: PRIMARY_LIGHT,
      dark: PRIMARY_DARK,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: SECONDARY,
      light: '#DAA520',
      dark: '#8B6914',
      contrastText: '#FFFFFF',
    },
    background: { default: CANVAS, paper: SURFACE },
    text: { primary: INK, secondary: MUTED, disabled: '#A0AEC0' },
    divider: HAIRLINE,
    success: { main: '#2F855A', light: '#68D391', dark: '#22543D', contrastText: '#FFFFFF' },
    warning: { main: '#C05621', light: '#F6AD55', dark: '#9C4221', contrastText: '#FFFFFF' },
    error: { main: '#C53030', light: '#FC8181', dark: '#9B2C2C', contrastText: '#FFFFFF' },
    info: { main: '#2B6CB0', light: '#63B3ED', dark: '#2A4365', contrastText: '#FFFFFF' },
    grey: {
      50: '#F7F8FA', 100: '#EDF1F5', 200: '#E2E8F0', 300: '#CBD5E0',
      400: '#A0AEC0', 500: '#718096', 600: '#4A5568', 700: '#2D3748',
      800: '#1A202C', 900: '#171923',
    },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"Inter", "Roboto", system-ui, -apple-system, "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.25rem', fontWeight: 700, color: INK, letterSpacing: '-0.02em', lineHeight: 1.15 },
    h2: { fontSize: '1.875rem', fontWeight: 700, color: INK, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h3: { fontSize: '1.5rem', fontWeight: 700, color: INK, letterSpacing: '-0.01em', lineHeight: 1.25 },
    h4: { fontSize: '1.25rem', fontWeight: 700, color: INK, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h5: { fontSize: '1.125rem', fontWeight: 600, color: INK, lineHeight: 1.35 },
    h6: { fontSize: '1rem', fontWeight: 600, color: INK, lineHeight: 1.4 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 600, color: INK_SOFT, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 600, color: INK_SOFT, lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', color: INK_SOFT, lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', color: MUTED, lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', color: SUBTLE, lineHeight: 1.4 },
    overline: {
      fontSize: '0.6875rem', fontWeight: 700, color: SUBTLE,
      letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.2,
    },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: CANVAS, color: INK, WebkitFontSmoothing: 'antialiased' },
        '*:focus-visible': { outline: 'none' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${HAIRLINE}`,
          backgroundImage: 'none',
          boxShadow: shadow.sm,
          transition: 'box-shadow 160ms ease, border-color 160ms ease',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '20px 24px 8px' },
        title: { fontSize: '1rem', fontWeight: 700, color: INK },
        subheader: { fontSize: '0.8125rem', color: SUBTLE },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: '20px 24px', '&:last-child': { paddingBottom: 20 } } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          padding: '8px 18px',
          minHeight: 40,
        },
        sizeLarge: { padding: '12px 24px', minHeight: 48, fontSize: '0.9375rem' },
        sizeSmall: { padding: '5px 12px', minHeight: 32, fontSize: '0.8125rem' },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: `0 8px 16px -4px ${alpha(PRIMARY, 0.35)}` },
          '&.Mui-disabled': { backgroundColor: '#E2E8F0', color: '#A0AEC0' },
        },
        outlined: {
          borderColor: HAIRLINE,
          color: INK,
          '&:hover': { borderColor: PRIMARY, backgroundColor: alpha(PRIMARY, 0.04) },
        },
        text: { '&:hover': { backgroundColor: alpha(PRIMARY, 0.06) } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': { backgroundColor: alpha(INK, 0.04) },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { border: `1px solid ${HAIRLINE}` },
        elevation1: { boxShadow: shadow.sm },
        elevation2: { boxShadow: shadow.md },
        elevation3: { boxShadow: shadow.lg },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: {
          backgroundColor: SURFACE,
          color: INK,
          borderBottom: `1px solid ${HAIRLINE}`,
          boxShadow: 'none',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: { root: { minHeight: 64, '@media (min-width:600px)': { minHeight: 64 } } },
    },
    MuiDrawer: {
      styleOverrides: {
        // Sidebar.tsx paints its own layered gradient + radial highlights on
        // top of this; keep this as a safe fallback so nothing flashes light
        // during first paint.
        paper: { backgroundColor: SIDEBAR_BG, color: '#F7F8FA', borderRight: 'none' },
      },
    },
    MuiListItemText: {
      defaultProps: {
        secondaryTypographyProps: {
          sx: {
            color: INK_SOFT,
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.5,
            mt: 0.25,
          },
        },
      },
      styleOverrides: {
        secondary: {
          color: INK_SOFT,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          marginTop: 2,
        },
      },
    },
    // NOTE: The Sidebar paints its own ListItemButton + ListItemIcon styles
    // (modernized pill with gradient + accent bar). We intentionally keep
    // these global overrides minimal so they don't conflict with the sidebar.
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 40 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 600, fontSize: '0.75rem', height: 24 },
        sizeMedium: { height: 28 },
        label: { paddingLeft: 10, paddingRight: 10 },
        outlined: { borderColor: HAIRLINE },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'medium' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: SURFACE,
          '& fieldset': { borderColor: HAIRLINE },
          '&:hover fieldset': { borderColor: '#A0AEC0' },
          '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 2 },
          '&.Mui-focused': { boxShadow: shadow.focus },
        },
        input: { padding: '12px 14px', fontSize: '0.9375rem', color: INK },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '0.9375rem', color: MUTED, '&.Mui-focused': { color: PRIMARY_DARK } } },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: '0.75rem', marginLeft: 4, marginTop: 6 } },
    },
    MuiSelect: { styleOverrides: { select: { padding: '12px 14px' } } },
    MuiTable: {
      styleOverrides: { root: { backgroundColor: SURFACE } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#EDF1F5',
          '& .MuiTableCell-root': {
            color: INK_SOFT,
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${HAIRLINE}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${HAIRLINE}`, fontSize: '0.875rem', color: INK_SOFT, padding: '14px 16px' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 120ms ease',
          '&:hover': { backgroundColor: alpha(PRIMARY, 0.03) },
          '&:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44, borderBottom: `1px solid ${HAIRLINE}` },
        indicator: { height: 3, borderRadius: '3px 3px 0 0' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: MUTED,
          padding: '10px 16px',
          '&.Mui-selected': { color: INK },
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16, boxShadow: shadow.lg } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 700, padding: '20px 24px 8px' } },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px 20px' } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '12px 24px 20px' } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: INK,
          fontSize: '0.75rem',
          padding: '6px 10px',
          borderRadius: 6,
          fontWeight: 500,
        },
        arrow: { color: INK },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontSize: '0.875rem', alignItems: 'center' },
        standardError: { backgroundColor: '#FEF2F2', color: '#9B2C2C' },
        standardWarning: { backgroundColor: '#FFFBEB', color: '#9C4221' },
        standardInfo: { backgroundColor: '#EBF5FF', color: '#2A4365' },
        standardSuccess: { backgroundColor: '#F0FDF4', color: '#22543D' },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: HAIRLINE } } },
    MuiAvatar: { styleOverrides: { root: { fontWeight: 600, fontSize: '0.875rem' } } },
    MuiBadge: {
      styleOverrides: { badge: { fontWeight: 600, fontSize: '0.6875rem', minWidth: 18, height: 18, padding: '0 5px' } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { height: 6, borderRadius: 3, backgroundColor: HAIRLINE } },
    },
    MuiSkeleton: { styleOverrides: { root: { backgroundColor: alpha(INK, 0.06) } } },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 6,
          border: `1px solid ${HAIRLINE}`,
          boxShadow: shadow.lg,
          borderRadius: 10,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderRadius: 6,
          margin: '2px 4px',
          '&:hover': { backgroundColor: alpha(PRIMARY, 0.06) },
        },
      },
    },
    MuiCheckbox: { styleOverrides: { root: { color: '#CBD5E0', '&.Mui-checked': { color: PRIMARY_DARK } } } },
    MuiRadio: { styleOverrides: { root: { color: '#CBD5E0', '&.Mui-checked': { color: PRIMARY_DARK } } } },
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 7 },
        thumb: { boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
        track: { borderRadius: 20, backgroundColor: '#CBD5E0', opacity: 1 },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: { '.metric-card &': { color: '#FFFFFF !important' } },
      },
    },
  },
});

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (token) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, token]);

  return <AppRoutes />;
};

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;